import React, { useRef, useEffect } from 'react';
import { View, Easing, Animated, StyleSheet } from 'react-native';

import { ViewPropTypes } from 'deprecated-react-native-prop-types';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';

import FoundationTestKeys from '../../FoundationTestKeys';
import { usePrevious } from '../../Utils/Hooks';
import { colorPropType } from '../../Utils/PropTypesValidators';
import { Colors, LightThemeColors, withTheme } from '../Themes';

import { TypeEnum, AnimationTypeEnum } from './PercentageIndicator.Enums';
import styles from './PercentageIndicator.Styles';

function getContainerStyle(containerStyle, fillColor, borderRadius, type) {
  const typeContainerStyle =
    type === TypeEnum.VERTICAL ? styles.verticalContainerStyle : styles.horizontalContainerStyle;
  const stylesArr = [styles.hiddenOverflow, typeContainerStyle, containerStyle];

  stylesArr.push({ backgroundColor: fillColor, borderRadius });

  return StyleSheet.flatten(stylesArr);
}

function getProgressStyle(style, color) {
  const stylesArr = [styles.progressStyle, style];

  stylesArr.push({ backgroundColor: color });

  return StyleSheet.flatten(stylesArr);
}

const getContainerStyleMemoized = memoize(getContainerStyle);
const getProgressStyleMemoized = memoize(getProgressStyle);
const renderDivider = (value, type, theme) => {
  if (value < 100) {
    return (
      <View
        style={[
          styles.dividerContainer(theme),
          type === TypeEnum.HORIZONTAL
            ? styles.dividerHorizontalStyle
            : styles.dividerVerticalStyle,
        ]}
        testID={FoundationTestKeys.percentageIndicatorDividerView}
      />
    );
  }
};
function PercentageIndicator({
  type,
  value,
  duration,
  delay,
  easing,
  color,
  fillColor,
  borderRadius,
  animate,
  isAnimated,
  containerStyle,
  style,
  progressAccessibilityLabel,
  animationType,
  theme,
  ...props
}) {
  const animation = useRef(new Animated.Value(1));
  const previousAnimate = usePrevious(animate);

  useEffect(() => {
    if (isAnimated && !previousAnimate && animate) {
      Animated.timing(animation.current, {
        toValue: 0,
        duration,
        easing,
        delay,
        useNativeDriver: false,
      }).start();
    } else if (!isAnimated) {
      animation.current.setValue(0);
    }
  }, [animate, delay, duration, easing, isAnimated, previousAnimate]);

  const outputRange =
    animationType === AnimationTypeEnum.SCALEDOWN ? [`${value}%`, '100%'] : [`${value}%`, '0%'];
  const animatedStyles =
    type === TypeEnum.HORIZONTAL
      ? {
        width: animation.current.interpolate({
          inputRange: [0, 1],
          outputRange,
        }),
      }
      : {
        height: animation.current.interpolate({
          inputRange: [0, 1],
          outputRange,
        }),
      };

  return (
    <View
      style={[getContainerStyleMemoized(containerStyle, fillColor, borderRadius, type), { cursor: 'pointer' }]}
      {...props}
      testID={FoundationTestKeys.percentageIndicatorView}
    >
      <Animated.View
        style={[getProgressStyleMemoized(style, color), animatedStyles, { height: `${value}%` }]}
        accessibilityLabel={progressAccessibilityLabel}
        testID={FoundationTestKeys.percentageIndicatorProgressView}
      >
        {renderDivider(value, type, theme)}
      </Animated.View>
    </View>
  );
}

PercentageIndicator.defaultProps = {
  animationType: AnimationTypeEnum.SCALEDOWN,
  type: TypeEnum.HORIZONTAL,
  value: 0,
  duration: 500,
  easing: Easing.ease,
  delay: 0,
  color: Colors.vfRed,
  fillColor: Colors.lightGrayTwo,
  borderRadius: 2.5,
  isAnimated: true,
  theme: LightThemeColors,
};

PercentageIndicator.propTypes = {
  animationType: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  animate: PropTypes.bool,
  duration: PropTypes.number,
  delay: PropTypes.number,
  easing: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  color: colorPropType,
  fillColor: colorPropType,
  borderRadius: PropTypes.number,
  isAnimated: PropTypes.bool,
  containerStyle: ViewPropTypes.style,
  style: ViewPropTypes.style,
  progressAccessibilityLabel: ViewPropTypes.accessibilityLabel,
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
};

export default withTheme(PercentageIndicator);
export { renderDivider };
